/**
 * useCatalogo.js - Scriptus Pharma Partner
 * Hook centralizado para gerenciamento do Catálogo de Produtos
 * 
 * ✅ Similar ao useScriptoteca do sistema médico
 * ✅ Estado centralizado
 * ✅ CRUD completo
 * ✅ Cache e otimizações
 * ✅ Tratamento de erros robusto
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import catalogoApi from '../services/catalogoApi';

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================
export function useCatalogo() {
  const { user, pharmacy, isPartner, auth } = useAuth();

  // ============================================================================
  // ESTADOS
  // ============================================================================
  const [produtos, setProdutos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState({
    produtos: true,
    stats: false,
    salvando: false,
    removendo: false,
    atualizando: false
  });
  const [error, setError] = useState(null);

  // ============================================================================
  // REFS
  // ============================================================================
  const mountedRef = useRef(true);
  const cacheRef = useRef({
    produtos: null,
    stats: null,
    lastFetch: null
  });
  const loadingRef = useRef(false);

  // ============================================================================
  // DADOS DA FARMÁCIA
  // ============================================================================
  const dadosFarmacia = useMemo(() => ({
    nome: pharmacy?.companyData?.tradeName || 
          pharmacy?.companyData?.name || 
          pharmacy?.tradeName || 
          pharmacy?.name || 
          'Sua Farmácia',
    id: pharmacy?.id || user?.uid,
    isPartner: Boolean(isPartner)
  }), [pharmacy, user?.uid, isPartner]);

  // ============================================================================
  // CLEANUP
  // ============================================================================
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ============================================================================
  // CARREGAR PRODUTOS
  // ============================================================================
  const carregarProdutos = useCallback(async (forceRefresh = false) => {
    if (!user?.uid || !auth || !isPartner) {
      setLoading(prev => ({ ...prev, produtos: false }));
      return;
    }

    // Evitar chamadas duplicadas
    if (loadingRef.current && !forceRefresh) {
      return;
    }

    // Usar cache se disponível e não forçar refresh
    const cacheAge = Date.now() - (cacheRef.current.lastFetch || 0);
    if (!forceRefresh && cacheRef.current.produtos && cacheAge < 30000) {
      setProdutos(cacheRef.current.produtos);
      setLoading(prev => ({ ...prev, produtos: false }));
      return;
    }

    loadingRef.current = true;
    setLoading(prev => ({ ...prev, produtos: true }));
    setError(null);

    try {
      const response = await catalogoApi.listar(auth);
      
      if (mountedRef.current && response.success) {
        const produtosOrdenados = (response.produtos || []).sort((a, b) => {
          // Ordenar por ordem se existir, senão por data
          if (a.ordem !== undefined && b.ordem !== undefined) {
            return a.ordem - b.ordem;
          }
          return new Date(b.criadoEm) - new Date(a.criadoEm);
        });
        
        setProdutos(produtosOrdenados);
        cacheRef.current.produtos = produtosOrdenados;
        cacheRef.current.lastFetch = Date.now();
        
        // Atualizar stats se veio junto
        if (response.stats) {
          setStats(response.stats);
          cacheRef.current.stats = response.stats;
        }
      }
    } catch (err) {
      console.error('[useCatalogo.carregarProdutos]', err);
      if (mountedRef.current) {
        setError({ message: err.message || 'Erro ao carregar produtos', type: 'load' });
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        setLoading(prev => ({ ...prev, produtos: false }));
      }
    }
  }, [user?.uid, auth, isPartner]);

  // ============================================================================
  // CARREGAR ESTATÍSTICAS
  // ============================================================================
  const carregarStats = useCallback(async () => {
    if (!user?.uid || !auth || !isPartner) return;

    setLoading(prev => ({ ...prev, stats: true }));

    try {
      const response = await catalogoApi.stats(auth);
      
      if (mountedRef.current && response?.success) {
        setStats(response.stats);
        cacheRef.current.stats = response.stats;
      }
    } catch (err) {
      console.error('[useCatalogo.carregarStats]', err);
    } finally {
      if (mountedRef.current) {
        setLoading(prev => ({ ...prev, stats: false }));
      }
    }
  }, [user?.uid, auth, isPartner]);

  // ============================================================================
  // CRIAR PRODUTO
  // ============================================================================
  const criarProduto = useCallback(async (dados) => {
    if (!user?.uid || !auth) {
      throw new Error('Usuário não autenticado');
    }

    setLoading(prev => ({ ...prev, salvando: true }));
    setError(null);

    try {
      const response = await catalogoApi.criar(auth, dados);
      
      if (response.success && response.produto) {
        // Adicionar ao estado local
        if (mountedRef.current) {
          setProdutos(prev => [response.produto, ...prev]);
          
          // Atualizar cache
          cacheRef.current.produtos = [response.produto, ...(cacheRef.current.produtos || [])];
          cacheRef.current.lastFetch = Date.now();
        }
        
        return response;
      }
      
      throw new Error('Resposta inválida do servidor');
    } catch (err) {
      console.error('[useCatalogo.criarProduto]', err);
      if (mountedRef.current) {
        setError({ message: err.message || 'Erro ao criar produto', type: 'create' });
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setLoading(prev => ({ ...prev, salvando: false }));
      }
    }
  }, [user?.uid, auth]);

  // ============================================================================
  // ATUALIZAR PRODUTO
  // ============================================================================
  const atualizarProduto = useCallback(async (id, dados) => {
    if (!user?.uid || !auth || !id) {
      throw new Error('Dados inválidos');
    }

    setLoading(prev => ({ ...prev, salvando: true }));
    setError(null);

    try {
      const response = await catalogoApi.atualizar(auth, id, dados);
      
      if (response.success && response.produto) {
        // Atualizar no estado local
        if (mountedRef.current) {
          setProdutos(prev => prev.map(p => 
            p.id === id ? { ...p, ...response.produto } : p
          ));
          
          // Atualizar cache
          if (cacheRef.current.produtos) {
            cacheRef.current.produtos = cacheRef.current.produtos.map(p =>
              p.id === id ? { ...p, ...response.produto } : p
            );
          }
        }
        
        return response;
      }
      
      throw new Error('Resposta inválida do servidor');
    } catch (err) {
      console.error('[useCatalogo.atualizarProduto]', err);
      if (mountedRef.current) {
        setError({ message: err.message || 'Erro ao atualizar produto', type: 'update' });
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setLoading(prev => ({ ...prev, salvando: false }));
      }
    }
  }, [user?.uid, auth]);

  // ============================================================================
  // REMOVER PRODUTO
  // ============================================================================
  const removerProduto = useCallback(async (id) => {
    if (!user?.uid || !auth || !id) {
      throw new Error('Dados inválidos');
    }

    setLoading(prev => ({ ...prev, removendo: true }));
    setError(null);

    try {
      await catalogoApi.remover(auth, id);
      
      // Remover do estado local
      if (mountedRef.current) {
        setProdutos(prev => prev.filter(p => p.id !== id));
        
        // Atualizar cache
        if (cacheRef.current.produtos) {
          cacheRef.current.produtos = cacheRef.current.produtos.filter(p => p.id !== id);
        }
      }
      
      return { success: true };
    } catch (err) {
      console.error('[useCatalogo.removerProduto]', err);
      if (mountedRef.current) {
        setError({ message: err.message || 'Erro ao remover produto', type: 'delete' });
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setLoading(prev => ({ ...prev, removendo: false }));
      }
    }
  }, [user?.uid, auth]);

  // ============================================================================
  // TOGGLE DISPONIBILIDADE
  // ============================================================================
  const toggleDisponibilidade = useCallback(async (id) => {
    if (!user?.uid || !auth || !id) {
      throw new Error('Dados inválidos');
    }

    try {
      const response = await catalogoApi.toggleDisponibilidade(auth, id);
      
      // Atualizar no estado local
      if (mountedRef.current && response.success !== false) {
        setProdutos(prev => prev.map(p => 
          p.id === id ? { ...p, disponivel: response.disponivel } : p
        ));
        
        // Atualizar cache
        if (cacheRef.current.produtos) {
          cacheRef.current.produtos = cacheRef.current.produtos.map(p =>
            p.id === id ? { ...p, disponivel: response.disponivel } : p
          );
        }
      }
      
      return response;
    } catch (err) {
      console.error('[useCatalogo.toggleDisponibilidade]', err);
      if (mountedRef.current) {
        setError({ message: err.message || 'Erro ao atualizar disponibilidade', type: 'toggle' });
      }
      throw err;
    }
  }, [user?.uid, auth]);

  // ============================================================================
  // REORDENAR PRODUTOS
  // ============================================================================
  const reordenarProdutos = useCallback(async (novaOrdem) => {
    if (!user?.uid || !auth || !Array.isArray(novaOrdem)) {
      return;
    }

    // Atualizar estado local imediatamente (otimistic update)
    const produtosReordenados = novaOrdem.map((id, index) => {
      const produto = produtos.find(p => p.id === id);
      return produto ? { ...produto, ordem: index } : null;
    }).filter(Boolean);

    setProdutos(produtosReordenados);

    try {
      // Persistir no backend (se endpoint existir)
      await catalogoApi.reordenar(auth, novaOrdem.map(p => typeof p === 'string' ? p : p.id));
      
      // Atualizar cache
      cacheRef.current.produtos = produtosReordenados;
    } catch (err) {
      console.error('[useCatalogo.reordenarProdutos]', err);
      // Não reverter em caso de erro - a ordem local já foi atualizada
    }
  }, [user?.uid, auth, produtos]);

  // ============================================================================
  // REFRESH
  // ============================================================================
  const refresh = useCallback(async () => {
    setLoading(prev => ({ ...prev, atualizando: true }));
    
    // Limpar cache
    cacheRef.current = {
      produtos: null,
      stats: null,
      lastFetch: null
    };
    
    try {
      await Promise.all([
        carregarProdutos(true),
        carregarStats()
      ]);
    } finally {
      if (mountedRef.current) {
        setLoading(prev => ({ ...prev, atualizando: false }));
      }
    }
  }, [carregarProdutos, carregarStats]);

  // ============================================================================
  // CLEAR ERROR
  // ============================================================================
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================================================
  // BUSCAR PRODUTO POR ID
  // ============================================================================
  const getProdutoById = useCallback((id) => {
    return produtos.find(p => p.id === id) || null;
  }, [produtos]);

  // ============================================================================
  // ESTATÍSTICAS CALCULADAS
  // ============================================================================
  const estatisticas = useMemo(() => {
    // Usar stats da API se disponível
    if (stats?.produtos) {
      return {
        total: stats.produtos.total || 0,
        disponiveis: stats.produtos.disponiveis || 0,
        indisponiveis: stats.produtos.indisponiveis || 0,
        totalVisualizacoes: stats.metricas?.visualizacoes || 0,
        totalCliques: stats.metricas?.cliques || 0,
        totalLeads: stats.metricas?.leads || 0,
        taxaConversao: stats.taxaConversao || 0
      };
    }

    // Calcular localmente
    const total = produtos.length;
    const disponiveis = produtos.filter(p => p.disponivel).length;
    const indisponiveis = total - disponiveis;
    const totalVisualizacoes = produtos.reduce((acc, p) => acc + (p.visualizacoes || 0), 0);
    const totalCliques = produtos.reduce((acc, p) => acc + (p.cliques || 0), 0);
    const totalLeads = produtos.reduce((acc, p) => acc + (p.leads || 0), 0);

    return {
      total,
      disponiveis,
      indisponiveis,
      totalVisualizacoes,
      totalCliques,
      totalLeads,
      taxaConversao: totalVisualizacoes > 0 ? ((totalLeads / totalVisualizacoes) * 100).toFixed(1) : 0
    };
  }, [produtos, stats]);

  // ============================================================================
  // CATEGORIAS DISPONÍVEIS
  // ============================================================================
  const categorias = useMemo(() => {
    const cats = [...new Set(produtos.map(p => p.categoria).filter(Boolean))];
    return cats.sort();
  }, [produtos]);

  // ============================================================================
  // EFEITO INICIAL
  // ============================================================================
  useEffect(() => {
    if (user?.uid && auth && isPartner) {
      carregarProdutos();
      carregarStats();
    } else {
      setLoading(prev => ({ ...prev, produtos: false }));
    }
  }, [user?.uid, auth, isPartner, carregarProdutos, carregarStats]);

  // ============================================================================
  // RETURN
  // ============================================================================
  return {
    // Dados
    produtos,
    stats,
    estatisticas,
    categorias,
    dadosFarmacia,
    
    // Estados
    loading,
    error,
    isLoading: loading.produtos || loading.stats,
    isReady: !loading.produtos && isPartner,
    isSaving: loading.salvando,
    isRemoving: loading.removendo,
    
    // Ações CRUD
    criarProduto,
    atualizarProduto,
    removerProduto,
    toggleDisponibilidade,
    reordenarProdutos,
    
    // Utilidades
    refresh,
    clearError,
    getProdutoById,
    carregarProdutos,
    carregarStats
  };
}

export default useCatalogo;